import { ReactElement, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { RightTabCount } from '@components/common/count/RightTabCount';
import { formatDateDDMMYY, isFilterValuePresent } from '@helper/utils';
import { useSiteListRxDb } from '@services/hooks/lms/siteList/useSiteListRxDb';
import { useSiteListFilterStore, SiteListFilterObject } from '@store/lms/siteListFilterStore';
import { useCompetitionFilterStore } from '@store/lms/competitionFilterStore';
import { contractorTypeFilterList } from '@components/lms/siteListWrapper/components/siteListFilters';
import { useSSDropdownQuery } from '@services/hooks/lms/addNewSite/useSSDropdownQuery';
import { useSSDropdownRxDb } from '@services/hooks/lms/addNewSite/useSSDropdownRxDb';

export const SiteListFilterTag = (): ReactElement => {
	const location = useLocation();
	const {
		siteListFilter: allSiteFilter,
		updateFilter: updateAllSiteFilter,
		resetFilter: resetAllSiteFilter,
	} = useSiteListFilterStore();
	const {
		competitionFilter,
		upcomingFilter,
		updateFilter: updateCompetitionFilter,
		resetFilter: resetCompetitionFilter,
	} = useCompetitionFilterStore();

	// Determine which filter to use based on route
	const { filterData, filterKey, updateFilter, resetFilter } = useMemo((): {
		filterData: SiteListFilterObject;
		filterKey: string;
		updateFilter: (key: string, filterObj: SiteListFilterObject) => void;
		resetFilter: (key: string) => void;
	} => {
		if (location.pathname.includes('/lms/sites/competition')) {
			return {
				filterData: competitionFilter,
				filterKey: 'competitionFilter',
				updateFilter: (key: string, filterObj: SiteListFilterObject): void => updateCompetitionFilter(key, filterObj),
				resetFilter: resetCompetitionFilter,
			};
		}
		if (location.pathname.includes('/lms/sites/upcoming')) {
			return {
				filterData: upcomingFilter,
				filterKey: 'upcomingFilter',
				updateFilter: (key: string, filterObj: SiteListFilterObject): void => updateCompetitionFilter(key, filterObj),
				resetFilter: resetCompetitionFilter,
			};
		}
		// All Sites route
		return {
			filterData: allSiteFilter,
			filterKey: 'siteListFilter',
			updateFilter: (_key: string, filterObj: SiteListFilterObject): void => updateAllSiteFilter(filterObj),
			resetFilter: resetAllSiteFilter,
		};
	}, [
		location.pathname,
		competitionFilter,
		upcomingFilter,
		allSiteFilter,
		updateCompetitionFilter,
		updateAllSiteFilter,
		resetCompetitionFilter,
		resetAllSiteFilter,
	]);

	const { totalCount } = useSiteListRxDb(filterData);
	// Ensure SS data is loaded and read from RxDB
	const hasSourceFilter = Boolean(filterData.source && filterData.source !== '');
	useSSDropdownQuery({ enabled: hasSourceFilter }); // Load data if needed
	const { data: ssList } = useSSDropdownRxDb(); // Read from RxDB

	const renderFilterValue = (key: string, value: string | object): string => {
		if (key === 'lastActivityDate') {
			if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
				const startDate = value[0];
				const endDate = value[1];
				return `${formatDateDDMMYY(startDate)} - ${formatDateDDMMYY(endDate)}`;
			}
		}
		if (key === 'date_of_next_visit') {
			if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
				const startDate = value[0];
				const endDate = value[1];
				return `${formatDateDDMMYY(startDate)} - ${formatDateDDMMYY(endDate)}`;
			}
		}
		if (key === 'createdAt') {
			if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
				const startDate = value[0];
				const endDate = value[1];
				return `${formatDateDDMMYY(startDate)} - ${formatDateDDMMYY(endDate)}`;
			}
		}
		if (key === 'contractorType' && value && typeof value === 'string') {
			return contractorTypeFilterList.find((opt) => opt.value === value)?.label ?? value;
		}
		if (key === 'convertStatus' && value) {
			return 'Converted Sites';
		}
		if (key === 'source' && value && typeof value === 'string') {
			// Find the site specialist name from the mdicode_c
			const selectedSS = ssList?.find((ss) => ss.mdicode_c === value);
			const displayName = selectedSS?.name ?? value;
			return `Site Specialist: ${displayName}`;
		}
		if (key === 'location') {
			if (
				value &&
				typeof value === 'object' &&
				'name' in value &&
				typeof value.name === 'string' &&
				value.name.length > 0
			) {
				return `Location: ${value.name}`;
			}
			return ''; // Return empty string for location if no valid name
		}
		return String(value);
	};

	const handleCross = (
		e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		key: string,
		currentFilterData: SiteListFilterObject
	): void => {
		e.preventDefault();
		let defaultValue;
		if (key === 'lastActivityDate' || key === 'createdAt' || key === 'date_of_next_visit') {
			defaultValue = [null, null];
		} else if (key === 'source') {
			defaultValue = '';
		} else if (key === 'location') {
			defaultValue = { lat: null, lng: null, name: '' };
		} else if (key === 'convertStatus') {
			defaultValue = false;
		} else {
			defaultValue = '';
		}
		updateFilter(filterKey, {
			...currentFilterData,
			[key]: defaultValue,
		});
	};

	const hasActiveFilters =
		filterData &&
		Object.entries(filterData).some(([key, value]) => {
			const shouldShowFilter =
				key === 'location'
					? value && typeof value === 'object' && value.name && value.name.length > 0
					: isFilterValuePresent(value);
			const shouldHideStatusFilter = key === 'site_current_status_c' && value === 'Upcoming';
			const shouldHideSolventFilter = key === 'solvent_used_c' && value === 'Competition';
			return shouldShowFilter && !shouldHideStatusFilter && !shouldHideSolventFilter;
		});

	if (!hasActiveFilters) {
		return (
			<div className="filter-tags">
				<RightTabCount count={totalCount} />
			</div>
		);
	}

	return (
		<div className="filter-tags">
			<ul className="tag-list">
				{Object.entries(filterData).map(([key, value]) => {
					const shouldShowFilter =
						key === 'location'
							? value && typeof value === 'object' && value.name && value.name.length > 0
							: isFilterValuePresent(value);

					// Hide the site_current_status_c filter when its value is 'Upcoming'
					const shouldHideStatusFilter = key === 'site_current_status_c' && value === 'Upcoming';
					const shouldHideSolventFilter = key === 'solvent_used_c' && value === 'Competition';

					if (shouldShowFilter && !shouldHideStatusFilter && !shouldHideSolventFilter) {
						return (
							<li key={key}>
								{`${renderFilterValue(key, value)}`}
								<NavLink
									to="#"
									aria-label="disable tag"
									onClick={(e) => {
										handleCross(e, key, filterData);
									}}
								>
									<span className="icon-cross"></span>
								</NavLink>
							</li>
						);
					}
					return null;
				})}
				{hasActiveFilters && (
					<NavLink to="#" aria-label="Clear All" className="clear-all-btn" onClick={() => resetFilter(filterKey)}>
						Clear All
					</NavLink>
				)}
			</ul>
			<RightTabCount count={totalCount} />
		</div>
	);
};
